// src/components/TranslationModal.jsx
// Reusable Claude-powered medical translation modal.
// Usage: <TranslationModal text={...} patient={...} contentType="clinical_note" onClose={...} />

import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { apiFetch } from "../lib/api";

export const SUPPORTED_LANGUAGES = [
  'Spanish', 'French', 'Portuguese', 'Mandarin Chinese', 'Cantonese',
  'Japanese', 'Korean', 'Vietnamese', 'Tagalog', 'Hindi', 'Urdu',
  'Arabic', 'Farsi', 'Russian', 'Ukrainian', 'Polish', 'German',
  'Italian', 'Somali', 'Swahili', 'Haitian Creole', 'Hmong',
  'Navajo', 'American Sign Language Notes',
];

const CONTENT_TYPE_LABELS = {
  clinical_note: 'Clinical Note',
  discharge_instructions: 'Discharge Instructions',
  patient_education: 'Patient Education',
  patient_message: 'Patient Message',
  prescription_instructions: 'Prescription Instructions',
  consent_form: 'Consent Form',
  other: 'Other',
};

export default function TranslationModal({ text, patient, contentType = 'other', onClose, onSaveToChart }) {
  const preferredLang = patient?.preferred_language && patient.preferred_language !== 'English'
    ? patient.preferred_language
    : null;

  const [targetLang, setTargetLang] = useState(
    preferredLang && SUPPORTED_LANGUAGES.includes(preferredLang)
      ? preferredLang
      : 'Spanish'
  );
  const [translated, setTranslated] = useState('');
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');
  const [saved, setSaved]           = useState(false);
  const [copied, setCopied]         = useState(false);

  // Auto-translate on open if text is present
  useEffect(() => {
    if (text) handleTranslate();
    // eslint-disable-next-line
  }, []);

  const handleTranslate = async () => {
    if (!text?.trim()) return;
    setLoading(true);
    setError('');
    setTranslated('');
    setSaved(false);
    try {
      const res = await apiFetch('translate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }, // apiFetch adds auth headers
        body: JSON.stringify({
          text,
          target_language: targetLang,
          content_type: contentType,
          patient_id: patient?.id,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Translation failed');
      setTranslated(data.translated_text);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!translated) return;
    try {
      await apiFetch('translate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }, // apiFetch adds auth headers
        body: JSON.stringify({
          text,
          target_language: targetLang,
          content_type: contentType,
          patient_id: patient?.id,
          save: true,
        }),
      });
      setSaved(true);
      if (onSaveToChart) onSaveToChart(translated, targetLang);
    } catch (err) {
      setError('Could not save: ' + err.message);
    }
  };

  const handleCopy = () => {
    if (!translated) return;
    navigator.clipboard.writeText(translated).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handlePrint = () => {
    const win = window.open('', '_blank');
    win.document.write(`
      <html><head><title>Translation — ${targetLang}</title>
      <style>body{font-family:sans-serif;padding:24px;line-height:1.6;} h2{color:#333;} .label{color:#666;font-size:12px;text-transform:uppercase;letter-spacing:0.05em;}</style>
      </head><body>
      <div class="label">${CONTENT_TYPE_LABELS[contentType] || 'Document'} · Translated to ${targetLang}</div>
      ${patient ? `<p><strong>Patient:</strong> ${patient.first || ''} ${patient.last || ''}</p>` : ''}
      <hr/>
      <h2>Original (English)</h2>
      <p style="white-space:pre-wrap">${text}</p>
      <hr/>
      <h2>${targetLang} Translation</h2>
      <p style="white-space:pre-wrap">${translated}</p>
      </body></html>
    `);
    win.document.close();
    win.print();
  };

  return createPortal(
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9100,
      background: 'rgba(0,0,0,0.65)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', padding: 16,
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: 'var(--navy-2)', border: '1px solid var(--border-2)',
        borderRadius: 12, width: '100%', maxWidth: 900,
        maxHeight: '90vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 18 }}>🌐</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-1)' }}>Medical Translation</div>
              {patient && <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{patient.first} {patient.last}</div>}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: 4 }}>✕</button>
        </div>

        {/* Language selector + translate */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 18px', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: 'var(--text-3)', whiteSpace: 'nowrap' }}>Translate to:</span>
          <select
            value={targetLang}
            onChange={e => setTargetLang(e.target.value)}
            style={{ background: 'var(--navy-3)', border: '1px solid var(--border-2)', borderRadius: 6, color: 'var(--text-1)', padding: '5px 10px', fontSize: 13, flex: '1 1 180px', maxWidth: 240 }}
          >
            {SUPPORTED_LANGUAGES.map(lang => (
              <option key={lang} value={lang}>{lang}</option>
            ))}
          </select>
          {preferredLang && preferredLang !== targetLang && (
            <button
              onClick={() => setTargetLang(preferredLang)}
              style={{ fontSize: 11, background: 'rgba(15,110,86,0.15)', color: 'var(--teal)', border: '1px solid var(--teal)', borderRadius: 6, padding: '4px 10px', cursor: 'pointer' }}
            >
              Use patient preference ({preferredLang})
            </button>
          )}
          <button
            onClick={handleTranslate}
            disabled={loading || !text?.trim()}
            style={{ fontSize: 12, fontWeight: 600, background: 'var(--teal)', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 16px', cursor: loading ? 'wait' : 'pointer', opacity: loading ? 0.7 : 1, whiteSpace: 'nowrap' }}
          >
            {loading ? '⏳ Translating…' : 'Translate'}
          </button>
        </div>

        {/* Body — side by side */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0, flex: 1, overflow: 'hidden', minHeight: 0 }}>
          {/* Source */}
          <div style={{ padding: 16, borderRight: '1px solid var(--border)', overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Original (English)</div>
            <div style={{ fontSize: 13, color: 'var(--text-1)', lineHeight: 1.6, whiteSpace: 'pre-wrap', flex: 1 }}>
              {text || <span style={{ color: 'var(--text-3)', fontStyle: 'italic' }}>No text provided</span>}
            </div>
          </div>

          {/* Translation */}
          <div style={{ padding: 16, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--teal)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{targetLang}</div>
            {loading && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-3)', fontSize: 13, paddingTop: 12 }}>
                <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⏳</span>
                Translating…
              </div>
            )}
            {error && <div style={{ fontSize: 13, color: '#f87171', background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: 6, padding: '8px 12px' }}>{error}</div>}
            {translated && !loading && (
              <div style={{ fontSize: 13, color: 'var(--text-1)', lineHeight: 1.6, whiteSpace: 'pre-wrap', flex: 1 }}>{translated}</div>
            )}
            {!translated && !loading && !error && (
              <div style={{ fontSize: 13, color: 'var(--text-3)', fontStyle: 'italic', paddingTop: 12 }}>Translation will appear here</div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 18px', borderTop: '1px solid var(--border)', flexWrap: 'wrap', gap: 8 }}>
          <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
            Powered by Claude AI · Review all translations before sharing with patients
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {translated && (
              <>
                <button onClick={handleCopy} style={{ fontSize: 12, background: 'var(--navy-3)', color: 'var(--text-2)', border: '1px solid var(--border-2)', borderRadius: 6, padding: '6px 12px', cursor: 'pointer' }}>
                  {copied ? '✓ Copied' : '📋 Copy'}
                </button>
                <button onClick={handlePrint} style={{ fontSize: 12, background: 'var(--navy-3)', color: 'var(--text-2)', border: '1px solid var(--border-2)', borderRadius: 6, padding: '6px 12px', cursor: 'pointer' }}>
                  🖨 Print
                </button>
                <button onClick={handleSave} disabled={saved} style={{ fontSize: 12, fontWeight: 600, background: saved ? 'var(--navy-3)' : 'var(--teal)', color: saved ? 'var(--teal)' : '#fff', border: `1px solid var(--teal)`, borderRadius: 6, padding: '6px 14px', cursor: saved ? 'default' : 'pointer', opacity: saved ? 0.8 : 1 }}>
                  {saved ? '✓ Saved to Chart' : '💾 Save to Chart'}
                </button>
              </>
            )}
            <button onClick={onClose} style={{ fontSize: 12, background: 'var(--navy-4)', color: 'var(--text-2)', border: '1px solid var(--border-2)', borderRadius: 6, padding: '6px 12px', cursor: 'pointer' }}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
